import { useEffect, useState, useMemo, useCallback, memo, useRef } from "react";
import {
  ArrowToLeft,
  ArrowToRightIcon,
  DashboardActive,
  DashboardInActive,
  HostActiveIcon,
  HostInActiveIcon,
  InboxActive,
  InboxInActive,
  LightIcon,
  NftsActive,
  NftsInActive,
  ReservationActive,
  ReservationInactive,
  TransactionsActive,
  TransactionsInActive,
  TravlerActive,
  TravlerInActive,
  TripsActive,
  TripsInActive,
} from "../AssetComponents/Images";
import { SelectionGroup, SelectionItem } from "./Selection/Selection";
import { useLocation, useNavigate } from "react-router-dom";
// Import mobile unselected icons
import DashboardMobileunselect from "../assets/Dashboardsvg/Mobilesvg/DashboardMobileunselect";
import ReservationUnselect from "../assets/Dashboardsvg/Mobilesvg/ReservationUnselect";
import Myrealestateunselected from "../assets/Dashboardsvg/Mobilesvg/Myrealestateunselected";
import TransactionUnlisted from "../assets/Dashboardsvg/Mobilesvg/TransactionUnlisted";
import InboxUnslect from "../assets/Dashboardsvg/Mobilesvg/InboxUnslect";
import Travleunselect from "../assets/Dashboardsvg/Mobilesvg/Travleunselect";
import FavouriteIconUnselect from "../assets/Dashboardsvg/Mobilesvg/FavouriteIconUnselect";
import FavouritesIconactive from "../assets/Dashboardsvg/Mobilesvg/FavouritesIconactive";

// Memoized icon wrapper to prevent unnecessary re-renders
const IconWrapper = memo(({ ActiveIcon, InactiveIcon, MobileIcon, isMobile, isActive, expanded }) => {
  if (isActive) {
    return <ActiveIcon />;
  }
  
  if (isMobile && MobileIcon) {
    return (
      <div className="p-[8px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
        <MobileIcon />
      </div>
    );
  }
  
  return <InactiveIcon />;
});

IconWrapper.displayName = "IconWrapper";

// Mobile-optimized sidebar item component (no SelectionGroup logic)
// Inactive icon always maintains container size in normal flow, active icon overlays
const MobileSidebarItem = memo(({ 
  isActive, 
  onClick, 
  ActiveIcon, 
  MobileIcon,
  label 
}) => {
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer rounded-[16px] flex items-center justify-center p-[6px] py-[8px] transition-colors duration-200 ${
        isActive
          ? "bg-[#F6F6F6]"
          : "bg-white hover:bg-[#F6F6F6]"
      }`}
    >
      {/* Inactive Icon - always in normal flow to maintain container size */}
      <div 
        className={`flex items-center justify-center transition-opacity duration-150 ease-in-out ${
          !isActive ? 'opacity-100 relative z-0' : 'opacity-0 relative z-0'
        }`}
      >
        <div className="p-[8px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
          <MobileIcon />
        </div>
      </div>
      
      {/* Active Icon - overlays when active, positioned absolutely */}
      <div 
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-in-out ${
          isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
        }`}
      >
        <ActiveIcon />
      </div>
    </div>
  );
});

MobileSidebarItem.displayName = "MobileSidebarItem";

// Desktop-optimized sidebar item component (similar to mobile but for desktop styling)
// Always renders both icons to prevent flash when switching between items
const DesktopSidebarItem = memo(({ 
  isActive, 
  onClick, 
  ActiveIcon, 
  InactiveIcon,
  label,
  expanded
}) => {
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer rounded-[16px] flex items-center justify-between p-[6px] py-[8px] transition-colors duration-200 ${
        isActive
          ? "bg-[#F6F6F6]"
          : "hover:bg-[#F6F6F6] bg-white"
      }`}
    >
      <div className="flex items-center relative flex-1 min-w-0">
        {/* Icon container - relative positioning for absolute children */}
        <div className="relative flex-shrink-0">
          {/* Inactive Icon - always in normal flow to maintain container size */}
          <div 
            className={`flex items-center justify-center transition-opacity duration-150 ease-in-out ${
              !isActive ? 'opacity-100 relative z-0' : 'opacity-0 relative z-0'
            }`}
          >
            <InactiveIcon />
          </div>
          
          {/* Active Icon - overlays when active, positioned absolutely */}
          <div 
            className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-in-out ${
              isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            <ActiveIcon />
          </div>
        </div>
        
        {expanded && (
          <div className={`ml-[8px] transition-opacity duration-150 ease-in-out whitespace-nowrap ${
            isActive ? 'text-black' : 'text-[#959595]'
          }`}>
            {label}
          </div>
        )}
      </div>
      
      {expanded && isActive && (
        <LightIcon className="mr-[6px] flex-shrink-0" />
      )}
    </div>
  );
});

DesktopSidebarItem.displayName = "DesktopSidebarItem";

export const SideBar = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize mobile state immediately to prevent flash (SSR-safe)
  const getInitialMobileState = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024; // lg breakpoint
    }
    return false; // Default to desktop for SSR
  };
  
  // Initialize state based on initial mobile detection to prevent flash
  const initialMobile = getInitialMobileState();
  const [isMobile, setIsMobile] = useState(initialMobile);
  const [expanded, setExpanded] = useState(!initialMobile); // Collapsed on mobile, expanded on desktop
  
  // Memoize path segments to avoid unnecessary recalculations
  const pathSegments = useMemo(() => location.pathname.split("/"), [location.pathname]);
  
  // Memoize mode and submode to prevent unnecessary re-renders
  const mode = useMemo(() => pathSegments[2], [pathSegments]);
  const submode = useMemo(() => pathSegments[3], [pathSegments]);
  
  // Memoize defaultItem calculation to prevent recalculation
  const hostDefaultItem = useMemo(() => {
    if (submode === "dashboard" || submode === "mint") return 0;
    if (submode === "reservations") return 1;
    if (submode === "nfts" || submode === "calendar-sync") return 2; // Calendar sync is part of NFT management
    if (submode === "transactions") return 3;
    if (submode === "calendar") return 4;
    if (submode === "inbox") return 5;
    return 0;
  }, [submode]);
  
  const travelerDefaultItem = useMemo(() => {
    if (submode === "dashboard") return 0;
    if (submode === "trips") return 1;
    if (submode === "transactions") return 2;
    if (submode === "inbox") return 3;
    return 0;
  }, [submode]);
  
  // Check if device is mobile - use ref to prevent unnecessary re-renders
  const isMobileRef = useRef(initialMobile);
  
  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      if (isMobileRef.current !== mobile) {
        isMobileRef.current = mobile;
        setIsMobile(mobile);
        // Update expanded state immediately to prevent flash
        setExpanded(!mobile);
      }
    };
    
    // Ensure state is correct on mount (in case of hydration mismatch)
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  // Update ref when state changes
  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);
  
  // Handle sidebar toggle - prevent expansion on mobile
  const handleToggle = useCallback(() => {
    if (!isMobileRef.current) {
      setExpanded(prev => !prev);
    }
  }, []);
  
  // Memoized navigation handlers
  const navigateToHostDashboard = useCallback(() => navigate("/dashboard/host/dashboard"), [navigate]);
  const navigateToHostReservations = useCallback(() => navigate("/dashboard/host/reservations"), [navigate]);
  const navigateToHostNfts = useCallback(() => navigate("/dashboard/host/nfts"), [navigate]);
  const navigateToHostTransactions = useCallback(() => navigate("/dashboard/host/transactions"), [navigate]);
  const navigateToHostCalendar = useCallback(() => navigate("/dashboard/host/calendar"), [navigate]);
  const navigateToHostInbox = useCallback(() => navigate("/dashboard/host/inbox"), [navigate]);
  const navigateToTravelerDashboard = useCallback(() => navigate("/dashboard/traveler/dashboard"), [navigate]);
  const navigateToTravelerTrips = useCallback(() => navigate("/dashboard/traveler/trips"), [navigate]);
  const navigateToTravelerTransactions = useCallback(() => navigate("/dashboard/traveler/transactions"), [navigate]);
  const navigateToTravelerInbox = useCallback(() => navigate("/dashboard/traveler/inbox"), [navigate]);

  // Helper function to check if a route is active (for mobile sidebar)
  const isRouteActive = useCallback((route) => {
    const currentPath = location.pathname;
    if (route === "/dashboard/host/dashboard" || route === "/dashboard/host/mint") {
      return currentPath === "/dashboard/host/dashboard" || currentPath === "/dashboard/host/mint";
    }
    return currentPath.startsWith(route);
  }, [location.pathname]);

  // Memoize sidebar classes to prevent recalculation
  const sidebarClasses = useMemo(() => {
    if (mode === "profile") return "hidden";
    
    const baseClasses = "bg-white h-full p-[10px] flex flex-col overflow-hidden border-0 shadow-none";
    // On mobile, always use fixed width to prevent size changes/flash
    // On desktop, use conditional width based on expanded state
    const widthClasses = isMobile 
      ? "w-[80px] flex-shrink-0" // Fixed width on mobile to prevent flash
      : expanded 
        ? "min-w-[300px]" 
        : "w-max flex-shrink-0";
    // Disable transitions on mobile to prevent visible changes
    const transitionClasses = isMobile ? "" : "transition-all duration-300 ease-in-out";
    
    return `${baseClasses} ${widthClasses} ${transitionClasses}`;
  }, [mode, expanded, isMobile]);

  return (
    <div className={sidebarClasses}>
      <>
        {mode === "host" && (
          <div
            className={
              expanded
                ? "flex items-center w-full justify-between"
                : "flex items-center w-full justify-center"
            }
          >
            {expanded && (
              <div className="flex items-center gap-[10px]">
                <HostActiveIcon />
                <div>Host's Dashboard</div>
              </div>
            )}

            {!isMobile && (
              <div
                onClick={handleToggle}
                className={
                  expanded
                    ? "cursor-pointer hover:translate-x-[-4px] p-[4px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)] transition-transform duration-200 ease-in-out"
                    : "cursor-pointer hover:translate-x-[4px] p-[4px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)] transition-transform duration-200 ease-in-out"
                }
              >
                {expanded ? <ArrowToLeft /> : <ArrowToRightIcon />}
              </div>
            )}
          </div>
        )}

        {mode === "traveler" && (
          <div
            className={
              expanded
                ? "flex items-center w-full justify-between"
                : "flex items-center w-full justify-center"
            }
          >
            {expanded && (
              <div className="flex items-center gap-[10px]">
                <TravlerActive />
                <div>Traveler's Dashboard</div>
              </div>
            )}

            {!isMobile && (
              <div
                onClick={handleToggle}
                className={
                  expanded
                    ? "cursor-pointer hover:translate-x-[-4px] p-[4px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)] transition-transform duration-200 ease-in-out"
                    : "cursor-pointer hover:translate-x-[4px] p-[4px] w-max rounded-full shadow-[-2px_-2px_6px_0px_rgba(253,255,255,0.80),2px_2px_6px_0px_rgba(187,195,206,0.60)] transition-transform duration-200 ease-in-out"
                }
              >
                {expanded ? <ArrowToLeft /> : <ArrowToRightIcon />}
              </div>
            )}
          </div>
        )}

        <div className="h-full justify-between flex flex-col">
          {mode === "host" && isMobile ? (
            // Mobile-optimized sidebar (no SelectionGroup logic)
            <div className="space-y-[4px] mt-[20px] flex-1 overflow-y-auto">
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/dashboard") || isRouteActive("/dashboard/host/mint")}
                onClick={navigateToHostDashboard}
                ActiveIcon={DashboardActive}
                MobileIcon={DashboardMobileunselect}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/reservations")}
                onClick={navigateToHostReservations}
                ActiveIcon={ReservationActive}
                MobileIcon={ReservationUnselect}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/nfts")}
                onClick={navigateToHostNfts}
                ActiveIcon={NftsActive}
                MobileIcon={Myrealestateunselected}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/transactions")}
                onClick={navigateToHostTransactions}
                ActiveIcon={TransactionsActive}
                MobileIcon={TransactionUnlisted}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/calendar")}
                onClick={navigateToHostCalendar}
                ActiveIcon={FavouritesIconactive}
                MobileIcon={FavouriteIconUnselect}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/host/inbox")}
                onClick={navigateToHostInbox}
                ActiveIcon={InboxActive}
                MobileIcon={InboxUnslect}
              />
            </div>
          ) : mode === "host" && (
            <div className={`space-y-[4px] mt-[20px] ${isMobile ? 'flex-1 overflow-y-auto' : ''}`}>
              <DesktopSidebarItem
                isActive={hostDefaultItem === 0}
                onClick={navigateToHostDashboard}
                ActiveIcon={DashboardActive}
                InactiveIcon={DashboardInActive}
                label="Dashboard"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={hostDefaultItem === 1}
                onClick={navigateToHostReservations}
                ActiveIcon={ReservationActive}
                InactiveIcon={ReservationInactive}
                label="Reservations"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={hostDefaultItem === 2}
                onClick={navigateToHostNfts}
                ActiveIcon={NftsActive}
                InactiveIcon={NftsInActive}
                label="Apartments"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={hostDefaultItem === 3}
                onClick={navigateToHostTransactions}
                ActiveIcon={TransactionsActive}
                InactiveIcon={TransactionsInActive}
                label="Transactions"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={hostDefaultItem === 4}
                onClick={navigateToHostCalendar}
                ActiveIcon={FavouritesIconactive}
                InactiveIcon={FavouriteIconUnselect}
                label="Calendar"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={hostDefaultItem === 5}
                onClick={navigateToHostInbox}
                ActiveIcon={InboxActive}
                InactiveIcon={InboxInActive}
                label="Inbox"
                expanded={expanded}
              />
            </div>
          )}
          {mode === "traveler" && isMobile ? (
            // Mobile-optimized sidebar (no SelectionGroup logic)
            <div className="space-y-[4px] mt-[20px] flex-1 overflow-y-auto">
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/traveler/dashboard")}
                onClick={navigateToTravelerDashboard}
                ActiveIcon={DashboardActive}
                MobileIcon={DashboardMobileunselect}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/traveler/trips")}
                onClick={navigateToTravelerTrips}
                ActiveIcon={TripsActive}
                MobileIcon={Travleunselect}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/traveler/transactions")}
                onClick={navigateToTravelerTransactions}
                ActiveIcon={TransactionsActive}
                MobileIcon={TransactionUnlisted}
              />
              <MobileSidebarItem
                isActive={isRouteActive("/dashboard/traveler/inbox")}
                onClick={navigateToTravelerInbox}
                ActiveIcon={InboxActive}
                MobileIcon={InboxUnslect}
              />
            </div>
          ) : mode === "traveler" && (
            <div className={`space-y-[4px] mt-[20px] ${isMobile ? 'flex-1 overflow-y-auto' : ''}`}>
              <DesktopSidebarItem
                isActive={travelerDefaultItem === 0}
                onClick={navigateToTravelerDashboard}
                ActiveIcon={DashboardActive}
                InactiveIcon={DashboardInActive}
                label="Dashboard"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={travelerDefaultItem === 1}
                onClick={navigateToTravelerTrips}
                ActiveIcon={TripsActive}
                InactiveIcon={TripsInActive}
                label="Trips"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={travelerDefaultItem === 2}
                onClick={navigateToTravelerTransactions}
                ActiveIcon={TransactionsActive}
                InactiveIcon={TransactionsInActive}
                label="Transactions"
                expanded={expanded}
              />
              <DesktopSidebarItem
                isActive={travelerDefaultItem === 3}
                onClick={navigateToTravelerInbox}
                ActiveIcon={InboxActive}
                InactiveIcon={InboxInActive}
                label="Inbox"
                expanded={expanded}
              />
            </div>
          )}
          {(mode === "host" || mode === "traveler") && isMobile ? (
            // Mobile-optimized role switcher with smooth transitions
            <div className="w-full px-[6px] py-[4px] space-y-[8px] items-center rounded-[14px] bg-white border-[2px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] flex-shrink-0 flex flex-col overflow-hidden">
              {/* Host Button */}
              <div
                onClick={navigateToHostDashboard}
                className={`relative py-[4px] cursor-pointer rounded-[10px] w-full flex justify-center transition-colors duration-200 ${
                  mode === "host"
                    ? "bg-[#f6f6f6] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    : "hover:bg-[#f6f6f6]"
                }`}
              >
                {/* Inactive Host Icon - always rendered to maintain container size */}
                <div 
                  className={`flex items-center justify-center transition-opacity duration-150 ease-in-out ${
                    mode !== "host" ? 'opacity-100 relative z-0' : 'opacity-0 relative z-0'
                  }`}
                >
                  <HostInActiveIcon />
                </div>
                
                {/* Active Host Icon - overlays when active */}
                <div 
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-in-out ${
                    mode === "host" ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                  }`}
                >
                  <HostActiveIcon />
                </div>
              </div>
              
              {/* Traveler Button */}
              <div
                onClick={navigateToTravelerDashboard}
                className={`relative py-[4px] cursor-pointer rounded-[10px] w-full flex justify-center transition-colors duration-200 ${
                  mode === "traveler"
                    ? "bg-[#f6f6f6] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    : "hover:bg-[#f6f6f6]"
                }`}
              >
                {/* Inactive Traveler Icon - always rendered to maintain container size */}
                <div 
                  className={`flex items-center justify-center transition-opacity duration-150 ease-in-out ${
                    mode !== "traveler" ? 'opacity-100 relative z-0' : 'opacity-0 relative z-0'
                  }`}
                >
                  <TravlerInActive />
                </div>
                
                {/* Active Traveler Icon - overlays when active */}
                <div 
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ease-in-out ${
                    mode === "traveler" ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                  }`}
                >
                  <TravlerActive />
                </div>
              </div>
            </div>
          ) : (mode === "host" || mode === "traveler") && (
            <div className="w-full flex-shrink-0 overflow-hidden">
              <SelectionGroup
                defaultItem={mode === "host" ? 0 : 1}
                className={
                  expanded
                    ? `w-full px-[6px] py-[4px] gap-[8px] flex items-center rounded-[14px] bg-white border-[2px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]`
                    : `w-full px-[6px] py-[4px] space-y-[8px] items-center rounded-[14px] bg-white border-[2px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]`
                }
              >
              <SelectionItem
                SelectedItem={
                  <div
                    className={
                      expanded
                        ? "py-[4px] cursor-pointer bg-[#f6f6f6] rounded-[10px] w-[130px] flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                        : "py-[4px] cursor-pointer bg-[#f6f6f6] rounded-[10px] w-max flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    }
                  >
                    <HostActiveIcon />
                    {expanded && (
                      <div className="text-black font-semibold">Host</div>
                    )}

                    <LightIcon />
                  </div>
                }
                UnselectedItem={
                  <div
                    onClick={navigateToHostDashboard}
                    className={
                      expanded
                        ? "py-[4px] gap-[10px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[130px] flex justify-center transition-colors duration-200 ease-in-out"
                        : "py-[4px] gap-[10px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-max flex justify-center transition-colors duration-200 ease-in-out"
                    }
                  >
                    <HostInActiveIcon />
                    {expanded && <div className="text-[#959595]">Host</div>}
                  </div>
                }
              />
              <SelectionItem
                SelectedItem={
                  <div
                    className={
                      expanded
                        ? "py-[4px] cursor-pointer bg-[#f6f6f6] rounded-[10px] w-[130px] flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                        : "py-[4px] cursor-pointer bg-[#f6f6f6] rounded-[10px] w-max flex justify-center gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                    }
                  >
                    <TravlerActive />
                    {expanded && (
                      <div className="text-black font-semibold">Traveler</div>
                    )}

                    <LightIcon />
                  </div>
                }
                UnselectedItem={
                  <div
                    onClick={navigateToTravelerDashboard}
                    className={
                      expanded
                        ? "py-[4px] gap-[10px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[130px] flex justify-center transition-colors duration-200 ease-in-out"
                        : "py-[4px] gap-[10px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-max flex justify-center transition-colors duration-200 ease-in-out"
                    }
                  >
                    <TravlerInActive />
                    {expanded && <div className="text-[#959595]">Traveler</div>}
                  </div>
                }
              />
              </SelectionGroup>
            </div>
          )}
        </div>
      </>
    </div>
  );
});
